import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Upload, Download, FileSpreadsheet, FileText, Users, Clock, Receipt, FileCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import LogoLoader from '@/components/LogoLoader';
import { formatDateForFilename } from '@/utils/date';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const IMPORT_CONFIG = [
  {
    value: 'clients',
    label: 'Clients',
    icon: Users,
    description:
      'Import client organizations with billing contacts, tax registration numbers, and preferred currency details.',
    columns: [
      {
        key: 'name',
        label: 'Client Name',
        required: true,
        description: 'Legal or trading name that appears on invoices and statements.',
        example: 'ACME Corp'
      },
      {
        key: 'contact_email',
        label: 'Billing Email',
        required: true,
        description: 'Primary billing inbox used for invoice delivery and reminders.',
        example: 'billing@acme.com'
      },
      {
        key: 'contact_phone',
        label: 'Billing Phone',
        required: false,
        description: 'Direct phone number for the billing contact (digits only or with country code).',
        example: '+1-555-1234'
      },
      {
        key: 'currency',
        label: 'Billing Currency',
        required: false,
        description: 'ISO currency code used for invoices (defaults to USD when omitted).',
        example: 'USD'
      },
      {
        key: 'tax_reg_no',
        label: 'Tax Registration No.',
        required: false,
        description: 'Client tax or VAT registration reference for compliance reporting.',
        example: 'TAX-001'
      },
      {
        key: 'billing_address',
        label: 'Billing Address',
        required: false,
        description: 'Postal address shown on invoices and statements.',
        example: '123 Business St, Springfield'
      }
    ]
  },
  {
    value: 'consultants',
    label: 'Consultants',
    icon: Users,
    description:
      'Import consultant profiles including compensation, bill rates, and HR commission information.',
    columns: [
      {
        key: 'name',
        label: 'Consultant Name',
        required: true,
        description: 'Full name for the consultant record.',
        example: 'John Doe'
      },
      {
        key: 'email',
        label: 'Consultant Email',
        required: true,
        description: 'Unique login and contact email address.',
        example: 'john@example.com'
      },
      {
        key: 'country',
        label: 'Country',
        required: false,
        description: 'Country of residence or contract jurisdiction.',
        example: 'USA'
      },
      {
        key: 'pay_currency',
        label: 'Pay Currency',
        required: false,
        description: 'ISO code for consultant compensation currency (defaults to USD).',
        example: 'USD'
      },
      {
        key: 'default_pay_rate',
        label: 'Default Pay Rate',
        required: true,
        description: 'Hourly pay rate to compensate the consultant (numeric, no currency symbols).',
        example: '50'
      },
      {
        key: 'default_bill_rate',
        label: 'Default Bill Rate',
        required: false,
        description: 'Standard hourly bill rate for clients (leave blank if set per-project).',
        example: '75'
      },
      {
        key: 'hr_consultant_id',
        label: 'HR Consultant ID',
        required: false,
        description: 'Internal identifier linking to an HR consultant who sourced the talent.',
        example: 'HR-1001'
      },
      {
        key: 'hr_commission_amount',
        label: 'HR Commission Amount',
        required: false,
        description: 'Fixed $ commission per hour payable to the HR consultant (numeric).',
        example: '2.5'
      },
      {
        key: 'skills',
        label: 'Skills',
        required: false,
        description: 'Comma-separated list of key skills wrapped in quotes to preserve commas.',
        example: '"Python,React,AWS"'
      }
    ]
  },
  {
    value: 'time_entries',
    label: 'Time Entries',
    icon: Clock,
    description: 'Import approved hours for consultants working on purchase orders or projects.',
    columns: [
      {
        key: 'consultant_id',
        label: 'Consultant ID',
        required: true,
        description: 'Unique identifier of the consultant submitting hours.',
        example: 'CONSULTANT_ID'
      },
      {
        key: 'project_id',
        label: 'Project ID',
        required: false,
        description: 'Project identifier when hours relate to a project (omit if using PO).',
        example: 'PROJECT_ID'
      },
      {
        key: 'po_id',
        label: 'PO ID',
        required: false,
        description: 'Purchase order identifier when tracking against a PO instead of a project.',
        example: 'PO-001'
      },
      {
        key: 'week_start',
        label: 'Week Start',
        required: true,
        description: 'Monday date for the work week in MM-DD-YYYY format.',
        example: '01-06-2025'
      },
      {
        key: 'hours',
        label: 'Hours',
        required: true,
        description: 'Total number of hours worked for the week (numeric, decimals allowed).',
        example: '40'
      },
      {
        key: 'notes',
        label: 'Notes',
        required: false,
        description: 'Optional context for approvers such as milestone or task details.',
        example: 'Weekly entry'
      },
      {
        key: 'approval_status',
        label: 'Approval Status',
        required: false,
        description: 'Leave blank to default to Pending, or set to Approved/Rejected.',
        example: 'Approved'
      }
    ]
  }
];

const ImportExport = () => {
  const { token, user } = useAuth();
  const [importing, setImporting] = useState(false);
  const [importType, setImportType] = useState('clients');
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [activeExportType, setActiveExportType] = useState(null);

  const dataTypes = IMPORT_CONFIG;

  const exportTypes = [
    { value: 'clients', label: 'Clients', icon: Users },
    { value: 'consultants', label: 'Consultants', icon: Users },
    { value: 'invoices', label: 'Invoices', icon: FileText },
    { value: 'receipts', label: 'Receipts', icon: Receipt },
    { value: 'time_entries', label: 'Time Entries', icon: Clock }
  ];

  const selectedImportType = dataTypes.find((type) => type.value === importType);
  const sampleRow = selectedImportType?.columns.map((column) => column.example ?? '') ?? [];

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setImporting(true);
    setImportProgress(10);
    setImportResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      setImportProgress(30);
      const response = await fetch(`${BACKEND_URL}/api/import/${importType}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      setImportProgress(70);
      const result = await response.json();
      
      if (response.ok) {
        setImportProgress(100);
        setImportResult(result);
        toast.success(`Successfully imported ${result.imported_count} records!`);
      } else {
        throw new Error(result.detail || 'Import failed');
      }
    } catch (error) {
      toast.error(error.message || 'Import failed');
      setImportResult({ success: false, error: error.message });
    } finally {
      setImporting(false);
      setTimeout(() => setImportProgress(0), 2000);
    }
  };

  const handleExport = async (dataType, format) => {
    const exportMeta = exportTypes.find((type) => type.value === dataType);
    setExporting(true);
    setActiveExportType(exportMeta?.label || dataType);
    try {
      const response = await fetch(`${BACKEND_URL}/api/export/${dataType}?format=${format}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const stamp = formatDateForFilename();
        a.download = `${dataType}_${stamp}.${format === 'csv' ? 'csv' : 'xlsx'}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success(`${dataType} exported successfully!`);
      } else {
        throw new Error('Export failed');
      }
    } catch (error) {
      toast.error('Export failed: ' + error.message);
    } finally {
      setExporting(false);
      setActiveExportType(null);
    }
  };

  const downloadTemplate = (type) => {
    const selectedType = dataTypes.find((item) => item.value === type);
    if (!selectedType) {
      toast.error('Template not available for the selected data type');
      return;
    }

    const csvHeaders = selectedType.columns.map((column) => column.key);
    const csvRow = selectedType.columns.map((column) => column.example ?? '');
    const template = [csvHeaders.join(','), csvRow.join(',')].join('\r\n');
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${type}_template.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    toast.success('Template downloaded!');
  };

  if (user?.role !== 'Admin' && user?.role !== 'Operations Manager') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-96">
          <CardContent className="pt-6 text-center">
            <FileCheck className="w-12 h-12 mx-auto text-orange-500 mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Access Limited</h2>
            <p className="text-gray-600">Import/Export requires Admin or Operations Manager role.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="relative space-y-6" data-testid="import-export-page">
      {(importing || exporting) && (
        <LogoLoader
          fullscreen
          message={
            importing
              ? `Importing ${selectedImportType?.label ?? 'data'}...`
              : `Preparing ${activeExportType ?? 'export'} download...`
          }
        >
          {importing && (
            <div className="mt-6 w-64">
              <Progress value={importProgress} className="h-2" />
              <p className="mt-2 text-sm font-medium text-utis-forest text-center">
                {importProgress}% complete
              </p>
            </div>
          )}
        </LogoLoader>
      )}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Import & Export</h1>
        <p className="text-gray-600 mt-1">Bulk data operations for efficient management</p>
      </div>

      <Tabs defaultValue="import" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="import">
            <Upload className="w-4 h-4 mr-2" />
            Import
          </TabsTrigger>
          <TabsTrigger value="export">
            <Download className="w-4 h-4 mr-2" />
            Export
          </TabsTrigger>
        </TabsList>

        {/* Import Tab */}
        <TabsContent value="import" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Upload className="w-5 h-5 mr-2 text-utis-forest" />
                Import Data from CSV
              </CardTitle>
              <CardDescription>
                Upload CSV files to quickly add multiple records. Download templates below for the correct format.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label>Select Data Type</Label>
                <Select value={importType} onValueChange={setImportType}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {dataTypes.map(type => {
                      const Icon = type.icon;
                      return (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center">
                            <Icon className="w-4 h-4 mr-2" />
                            {type.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <p className="text-sm text-gray-600 mt-2">
                  {selectedImportType?.description}
                </p>
              </div>

              {selectedImportType && (
                <div className="grid gap-4 pt-2 md:grid-cols-2">
                  {selectedImportType.columns.some((column) => column.required) && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Required Columns
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {selectedImportType.columns
                          .filter((column) => column.required)
                          .map((column) => (
                          <Badge
                            key={column.key}
                            variant="outline"
                            className="border-green-200 bg-green-50 text-green-700"
                          >
                            {column.key}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedImportType.columns.some((column) => !column.required) && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Optional Columns
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {selectedImportType.columns
                          .filter((column) => !column.required)
                          .map((column) => (
                          <Badge
                            key={column.key}
                            variant="outline"
                            className="border-gray-200 bg-gray-50 text-gray-700"
                          >
                            {column.key}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">Download CSV Template</h3>
                <p className="text-sm text-blue-800 mb-3">
                  Use our templates to ensure your CSV file has the correct columns and format.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadTemplate(importType)}
                  className="border-blue-300 text-blue-700 hover:bg-blue-100"
                  disabled={importing || exporting}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download {selectedImportType?.label} Template
                </Button>
                {selectedImportType && (
                  <div className="mt-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-blue-900">Column Guide</p>
                    <div className="mt-2 overflow-x-auto rounded-md border border-blue-200 bg-white">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-blue-50/70">
                            <TableHead className="w-40">Column</TableHead>
                            <TableHead className="w-28">Required?</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead className="w-48">Example</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedImportType.columns.map((column) => (
                            <TableRow key={column.key}>
                              <TableCell className="font-medium">{column.key}</TableCell>
                              <TableCell>
                                <Badge
                                  variant="outline"
                                  className={column.required ? 'border-green-200 bg-green-50 text-green-700' : 'border-gray-200 bg-gray-50 text-gray-700'}
                                >
                                  {column.required ? 'Required' : 'Optional'}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-sm text-gray-600">{column.description}</TableCell>
                              <TableCell>
                                <code className="rounded bg-blue-50 px-2 py-1 text-xs text-blue-900">
                                  {column.example || '—'}
                                </code>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    <p className="mt-3 text-xs text-blue-900">
                      Example row:&nbsp;
                      <code className="bg-white px-1 py-0.5 rounded border border-blue-200">
                        {sampleRow.join(',')}
                      </code>
                    </p>
                  </div>
                )}
              </div>

              <div>
                <Label>Upload CSV File</Label>
                <div className="mt-2 flex items-center justify-center w-full">
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 border-gray-300 hover:border-green-500">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <FileSpreadsheet className="w-10 h-10 mb-3 text-gray-400" />
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-xs text-gray-500">CSV files only</p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept=".csv"
                      onChange={handleImport}
                      disabled={importing || exporting}
                    />
                  </label>
                </div>
              </div>

              {importing && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Importing...</span>
                    <span className="font-medium text-utis-forest">{importProgress}%</span>
                  </div>
                  <Progress value={importProgress} className="h-2" />
                </div>
              )}

              {importResult && (
                <div className={`p-4 rounded-lg border ${importResult.success ? 'bg-utis-sage/10 border-utis-sage' : 'bg-red-50 border-red-200'}`}>
                  <h3 className={`font-semibold mb-2 ${importResult.success ? 'text-green-900' : 'text-red-900'}`}>
                    {importResult.success ? '✓ Import Complete' : '✗ Import Failed'}
                  </h3>
                  {importResult.success ? (
                    <div className="text-sm text-utis-forest">
                      <p>Successfully imported: <strong>{importResult.imported_count}</strong> out of <strong>{importResult.total_rows}</strong> records</p>
                      {importResult.errors && importResult.errors.length > 0 && (
                        <div className="mt-2">
                          <p className="font-medium">Errors ({importResult.errors.length}):</p>
                          <ul className="list-disc list-inside mt-1 max-h-32 overflow-y-auto">
                            {importResult.errors.map((error, idx) => (
                              <li key={idx} className="text-xs">{error}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-red-800">{importResult.error}</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Export Tab */}
        <TabsContent value="export" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Download className="w-5 h-5 mr-2 text-utis-forest" />
                Export Data
              </CardTitle>
              <CardDescription>
                Download your data in CSV or Excel format for backup, analysis, or reporting.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {exportTypes.map(type => {
                  const Icon = type.icon;
                  return (
                    <Card key={type.value} className="border-gray-200 hover:border-green-300 hover:shadow-md transition-all">
                      <CardContent className="pt-6">
                        <div className="flex items-center space-x-3 mb-4">
                          <div className="w-10 h-10 rounded-lg bg-utis-sage/20 flex items-center justify-center">
                            <Icon className="w-5 h-5 text-utis-forest" />
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900">{type.label}</h3>
                            <p className="text-xs text-gray-600">Download all records</p>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleExport(type.value, 'csv')}
                            disabled={importing || exporting}
                            className="flex-1"
                          >
                            <FileText className="w-3 h-3 mr-1" />
                            CSV
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleExport(type.value, 'excel')}
                            disabled={importing || exporting}
                            className="flex-1"
                          >
                            <FileSpreadsheet className="w-3 h-3 mr-1" />
                            Excel
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h3 className="font-semibold text-yellow-900 mb-2">💡 Export Tips</h3>
                <ul className="text-sm text-yellow-800 space-y-1">
                  <li>• <strong>CSV</strong>: Best for importing into other systems or quick data analysis</li>
                  <li>• <strong>Excel</strong>: Formatted spreadsheets with auto-sized columns, ideal for sharing</li>
                  <li>• <strong>Reports</strong>: Use the Reports page to export specific period-based reports as PDF</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ImportExport;
